import React from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function LegalPage() {
  const { theme } = useTheme();
  const { lang } = useLang();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textBody = isDark ? "text-slate-300" : "text-slate-600";
  const labelColor = isDark ? "text-amber-400" : "text-amber-600";

  const content = {
    fr: {
      title: "Mentions Légales",
      sections: [
        { h: "Éditeur du site", p: "DreamNoor\nSite web : dreamnoor.com\nEmail : contact@dreamnoor.com" },
        { h: "Hébergement", p: "Le site est hébergé par des services cloud professionnels assurant la disponibilité et la sécurité des données." },
        { h: "Propriété intellectuelle", p: "L'ensemble des éléments du site DreamNoor (textes, images, design, logo, code source) est protégé par le droit de la propriété intellectuelle. Toute reproduction, même partielle, est interdite sans autorisation préalable." },
        { h: "Données personnelles", p: "Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification et de suppression de vos données personnelles. Pour exercer ce droit, contactez : contact@dreamnoor.com\n\nPour plus de détails, consultez notre Politique de Confidentialité." },
        { h: "Cookies", p: "Le site utilise des cookies pour le fonctionnement technique (authentification, préférences) et pour la diffusion de publicités via Google AdSense. Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur." },
        { h: "Responsabilité", p: "Les interprétations de rêves fournies par DreamNoor sont générées par intelligence artificielle à titre informatif et spirituel uniquement. Elles ne constituent en aucun cas un avis médical, psychologique ou religieux. L'éditeur décline toute responsabilité quant à l'utilisation des interprétations fournies." },
        { h: "Liens externes", p: "Le site peut contenir des liens vers des sites tiers. DreamNoor n'est pas responsable du contenu de ces sites." },
        { h: "Droit applicable", p: "Les présentes mentions légales sont régies par le droit français." },
      ],
    },
    en: {
      title: "Legal Notice",
      sections: [
        { h: "Site Publisher", p: "DreamNoor\nWebsite: dreamnoor.com\nEmail: contact@dreamnoor.com" },
        { h: "Hosting", p: "The site is hosted by professional cloud services ensuring data availability and security." },
        { h: "Intellectual Property", p: "All elements of the DreamNoor site (texts, images, design, logo, source code) are protected by intellectual property law. Any reproduction, even partial, is prohibited without prior authorization." },
        { h: "Personal Data", p: "In accordance with GDPR, you have the right to access, rectify, and delete your personal data. To exercise this right, contact: contact@dreamnoor.com\n\nFor more details, see our Privacy Policy." },
        { h: "Cookies", p: "The site uses cookies for technical operation (authentication, preferences) and for advertising delivery via Google AdSense." },
        { h: "Liability", p: "Dream interpretations provided by DreamNoor are AI-generated for informational and spiritual purposes only. They do not constitute medical, psychological, or religious advice." },
        { h: "External Links", p: "The site may contain links to third-party sites. DreamNoor is not responsible for the content of these sites." },
        { h: "Governing Law", p: "These legal notices are governed by French law." },
      ],
    },
    es: {
      title: "Aviso Legal",
      sections: [
        { h: "Editor del sitio", p: "DreamNoor\nSitio web: dreamnoor.com\nEmail: contact@dreamnoor.com" },
        { h: "Alojamiento", p: "El sitio está alojado en servicios cloud profesionales que garantizan la disponibilidad y seguridad de los datos." },
        { h: "Propiedad intelectual", p: "Todos los elementos del sitio DreamNoor (textos, imágenes, diseño, logo, código fuente) están protegidos por la ley de propiedad intelectual. Cualquier reproducción está prohibida sin autorización previa." },
        { h: "Datos personales", p: "De acuerdo con el RGPD, tiene derecho a acceder, rectificar y eliminar sus datos personales. Contacto: contact@dreamnoor.com" },
        { h: "Cookies", p: "El sitio utiliza cookies para el funcionamiento técnico y para la publicidad a través de Google AdSense." },
        { h: "Responsabilidad", p: "Las interpretaciones de sueños proporcionadas por DreamNoor son generadas por IA con fines informativos y espirituales únicamente." },
        { h: "Enlaces externos", p: "El sitio puede contener enlaces a sitios de terceros. DreamNoor no es responsable del contenido de esos sitios." },
        { h: "Ley aplicable", p: "Este aviso legal se rige por la ley francesa." },
      ],
    },
    ar: {
      title: "الإشعار القانوني",
      sections: [
        { h: "ناشر الموقع", p: "دريم نور\nالموقع: dreamnoor.com\nالبريد: contact@dreamnoor.com" },
        { h: "الاستضافة", p: "يتم استضافة الموقع على خدمات سحابية احترافية تضمن توفر البيانات وأمانها." },
        { h: "الملكية الفكرية", p: "جميع عناصر موقع دريم نور (النصوص والصور والتصميم والشعار والكود) محمية بموجب قانون الملكية الفكرية. يُحظر أي نسخ بدون إذن مسبق." },
        { h: "البيانات الشخصية", p: "وفقاً للائحة العامة لحماية البيانات، لديك الحق في الوصول إلى بياناتك الشخصية وتصحيحها وحذفها. للتواصل: contact@dreamnoor.com" },
        { h: "ملفات تعريف الارتباط", p: "يستخدم الموقع ملفات تعريف الارتباط للتشغيل التقني وللإعلانات عبر Google AdSense." },
        { h: "المسؤولية", p: "تفسيرات الأحلام المقدمة من دريم نور مولدة بالذكاء الاصطناعي لأغراض معلوماتية وروحانية فقط." },
        { h: "الروابط الخارجية", p: "قد يحتوي الموقع على روابط لمواقع طرف ثالث. دريم نور غير مسؤول عن محتوى تلك المواقع." },
        { h: "القانون المطبق", p: "يخضع هذا الإشعار القانوني للقانون الفرنسي." },
      ],
    },
  };

  const c = content[lang] || content.fr;

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        <button onClick={() => navigate(-1)} className={`mb-4 p-2 rounded-full ${isDark ? "hover:bg-white/10" : "hover:bg-black/5"} transition-colors`}>
          <ArrowLeft size={20} className={textPrimary} />
        </button>
        <h1 className={`font-heading text-2xl font-bold ${textPrimary} mb-2`}>{c.title}</h1>
        <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"} mb-8`}>DreamNoor - dreamnoor.com</p>
        {c.sections.map((s, i) => (
          <div key={i} className="mb-6">
            <h2 className={`text-sm font-bold ${labelColor} mb-2`}>{s.h}</h2>
            <p className={`text-sm ${textBody} leading-relaxed whitespace-pre-line`}>{s.p}</p>
          </div>
        ))}
      </div>
      <BottomNav />
    </AppLayout>
  );
}
